// Web variant of `cache.ts`: swaps the react-native-fs disk cache for
// localStorage (still layered on top of an in-memory map). The exported API is
// identical so the shared fetchers (`stations.ts`, `news.ts`, `guide.ts`) work
// unchanged on the web.
export const CACHE_TTL_MS = 60 * 60 * 1000;

type CacheEntry<T> = {
  content: T;
  fetchedAt: string;
};

type WebStorage = {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
};

const memoryCache = new Map<string, CacheEntry<unknown>>();

const getStorage = (): WebStorage | null => {
  try {
    const storage = (globalThis as { localStorage?: WebStorage }).localStorage;

    if (!storage) {
      return null;
    }

    // Make sure localStorage is actually usable (sandboxed/private contexts).
    const probe = '__keyfiyat_cache_probe__';
    storage.setItem(probe, '1');
    storage.removeItem(probe);

    return storage;
  } catch {
    return null;
  }
};

const storageKey = (key: string): string => `cache:${key}`;

const isFresh = (entry: CacheEntry<unknown>): boolean =>
  Date.now() - Date.parse(entry.fetchedAt) < CACHE_TTL_MS;

const readEntryFromDisk = async <T>(
  key: string,
): Promise<CacheEntry<T> | null> => {
  try {
    const stored = getStorage()?.getItem(storageKey(key));

    if (!stored) {
      return null;
    }

    return JSON.parse(stored) as CacheEntry<T>;
  } catch (caughtError) {
    console.warn(`Failed to read the cached "${key}" content`, caughtError);
    return null;
  }
};

const writeEntryToDisk = async <T>(
  key: string,
  entry: CacheEntry<T>,
): Promise<void> => {
  try {
    getStorage()?.setItem(storageKey(key), JSON.stringify(entry));
  } catch (caughtError) {
    console.warn(`Failed to cache the "${key}" content`, caughtError);
  }
};

/**
 * Returns the resource identified by `key`. As long as a cached copy was
 * fetched less than CACHE_TTL_MS (one hour) ago, no new fetch is performed
 * and the cached content is returned instead. The cached content together
 * with its fetching datetime is kept in memory and persisted in localStorage
 * so it survives page reloads.
 */
export const fetchWithCache = async <T>(
  key: string,
  fetcher: () => Promise<T>,
): Promise<T> => {
  let entry: CacheEntry<T> | null | undefined = memoryCache.get(key) as
    | CacheEntry<T>
    | undefined;

  if (!entry) {
    entry = await readEntryFromDisk<T>(key);

    if (entry) {
      memoryCache.set(key, entry);
    }
  }

  if (entry && isFresh(entry)) {
    return entry.content;
  }

  const content = await fetcher();

  const freshEntry: CacheEntry<T> = {
    content,
    fetchedAt: new Date().toISOString(),
  };

  memoryCache.set(key, freshEntry);
  await writeEntryToDisk(key, freshEntry);

  return content;
};
